import { BehaviorSubject, Observable, filter, take } from 'rxjs';
import Hls from 'hls.js';
import hlsLogTypeEnum from './../media/hlsLogTypeEnum.mjs';

/**
 * @module hlsVideoService
 */
function createHlsVideoService(apiService, logService, environment, _window, util) {
    const moduleName = 'hlsVideoService';
    const hlsDevices = {};
    let _isSupported;

    function log(params) {
        params.module = moduleName;
        logService.log(params);
        return params;
    }

    function removeHlsDevice(hlsDevice) {
        if (hlsDevice) {
            if (hlsDevice.keepAlive) {
                _window.clearInterval(hlsDevice.keepAlive);
            }
            hlsDevice.keepAlive = undefined;
            hlsDevice.videoElement.pause();

            if (hlsDevice.hls) {
                // using hls.js lib, so destory instance
                hlsDevice.hls.detachMedia();
                hlsDevice.hls.stopLoad();
                hlsDevice.hls.destroy();
                //cleanup of hls device
                hlsDevice.hls = null;
            } else {
                // built-in Hls support
                hlsDevice.videoElement.src = '';
                hlsDevice.info.next({
                    type: hlsLogTypeEnum.builtInStop,
                    deviceId: hlsDevice.deviceId,
                    module: moduleName,
                    traceId: hlsDevice?.traceId
                });
                hlsDevice.info.complete();
                delete hlsDevices[hlsDevice.deviceId];
            }
        }
    }

    /**
     * @this The Hls instance
     * @param {String} event internal Hls event type
     * @param {Object} data details about the Hls instance
     */
    function onHlsError(event, data) {
        const hlsDevice = hlsDevices[this.deviceId];
        hlsDevice?.info.next({
            type: hlsLogTypeEnum.libError,
            error: data,
            deviceId: hlsDevice?.deviceId,
            module: moduleName,
            traceId: hlsDevice?.traceId,
            timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0)
        });
        log({
            code: hlsLogTypeEnum.libError,
            error: data,
            deviceId: hlsDevice?.deviceId,
            traceId: hlsDevice?.traceId,
            object: {
                messageIndex: hlsDevice?.messageIndex,
                timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0)
            }
        });
    }

    /**
     * @this The Hls instance
     * @param {String} event internal Hls event type
     * @param {Object} data details about the Hls instance
     */
    function onHlsAttached(/*event, data*/) {
        const hlsDevice = hlsDevices[this.deviceId];
        if (hlsDevice) {
            hlsDevice.info.next({
                type: hlsLogTypeEnum.libMediaAttached,
                deviceId: hlsDevice.deviceId,
                module: moduleName,
                traceId: hlsDevice?.traceId
            });
        }
    }

    /**
     * @this The Hls instance
     * @param {String} event internal Hls event type
     * @param {Object} data details about the Hls instance
     */
    function onHlsManifestParsed(/*event, data*/) {
        const hlsDevice = hlsDevices[this.deviceId];
        if (hlsDevice) {
            hlsDevice.info.next({
                type: hlsLogTypeEnum.libManifestParsed,
                deviceId: hlsDevice.deviceId,
                module: moduleName,
                traceId: hlsDevice?.traceId
            });
        }
    }

    /**
     * @this The Hls instance
     * @param {String} event internal Hls event type
     * @param {Object} data details about the Hls instance, mostly useless.
     *     See hls.js docs for details.
     * @param {*} data.type
     * @param {*} data.part
     * @param {*} data.chunkMeta
     * @param {*} data.parent
     * @param {*} data.frag
     * @param {*} data.timeRanges
     */
    function onHlsBufferAppended(event, data) {
        const hlsDevice = hlsDevices[this.deviceId];
        if (hlsDevice) {
            if (!hlsDevice.receivedFirstPacket) {
                hlsDevice.receivedFirstPacket = true;
                log({
                    code: hlsLogTypeEnum.firstPacketReceived,
                    type: 'info',
                    int: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                    deviceId: hlsDevice?.deviceId,
                    traceId: hlsDevice?.traceId
                });
                hlsDevice.info.next({
                    type: hlsLogTypeEnum.firstPacketReceived,
                    deviceId: hlsDevice?.deviceId,
                    module: moduleName,
                    traceId: hlsDevice?.traceId,
                    timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0)
                });
            }
            hlsDevice.messageIndex += 1;
            hlsDevice.info.next({
                type: hlsLogTypeEnum.info,
                messageSize: data?.chunkMeta?.size,
                messageIndex: hlsDevice.messageIndex,
                deviceId: hlsDevice.deviceId,
                timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                module: moduleName,
                traceId: hlsDevice?.traceId
            });
        }
    }

    /**
     * @this The Hls instance
     * @param {String} event internal Hls event type
     * @param {Object} data details about the Hls instance
     */
    function onHlsDestroying(/*event, data*/) {
        const hlsDevice = hlsDevices[this.deviceId];
        hlsDevice?.info.next({
            type: hlsLogTypeEnum.libDestroying,
            deviceId: hlsDevice?.deviceId,
            module: moduleName,
            traceId: hlsDevice?.traceId
        });
        try {
            _window.URL.revokeObjectURL(hlsDevice?.objectUrl);
        } catch (ignore) {
            //noop
        }

        delete hlsDevices[hlsDevice.deviceId];
    }

    function onCanPlay() {
        handleAutoPlay(hlsDevices[this.deviceId]);
    }

    function onVideoStalled(e) {
        const hlsDevice = hlsDevices[this.deviceId];
        hlsDevice?.info.next({
            type: hlsLogTypeEnum.videoStalled,
            deviceId: hlsDevice?.deviceId,
            error: e,
            module: moduleName,
            usingHlsLib: Boolean(hlsDevice?.hls),
            timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0),
            traceId: hlsDevice?.traceId
        });
        log({
            code: hlsLogTypeEnum.videoStalled,
            deviceId: hlsDevice?.deviceId,
            object: {
                usingHlsLib: Boolean(hlsDevice?.hls),
                messageIndex: hlsDevice?.messageIndex
            },
            int: _window.Date.now() - (hlsDevice?.startTime ?? 0),
            error: e,
            traceId: hlsDevice?.traceId
        });
    }

    /**
     * {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/error}
     * @param {MediaError} e
     */
    function onVideoError(e) {
        const hlsDevice = hlsDevices[this.deviceId];
        hlsDevice?.info?.next({
            type: hlsLogTypeEnum.videoError,
            deviceId: hlsDevice?.deviceId,
            error: e,
            module: moduleName,
            playerCode: hlsDevice?.videoElement?.error?.code,
            playerMessage: hlsDevice?.videoElement?.error?.message,
            timeSinceStart: hlsDevice?.videoElement.error.code,
            traceId: hlsDevice?.traceId,
            messageIndex: hlsDevice?.messageIndex
        });
        log({
            code: hlsLogTypeEnum.videoError,
            deviceId: hlsDevice?.deviceId,
            object: {
                playerCode: hlsDevice?.videoElement?.error?.code,
                playerMessage: hlsDevice?.videoElement?.error?.message,
                usingHlsLib: Boolean(hlsDevice?.hls),
                timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                receivedFirstPacket: hlsDevice?.receivedFirstPacket,
                messageIndex: hlsDevice?.messageIndex
            },
            error: e,
            int: hlsDevice?.videoElement?.error?.code,
            traceId: hlsDevice?.traceId
        });
    }

    /**
     * @description start playing automatically if possible, or if that didn't work,
     *     then autoplay is not supported or the user needs to give
     *     or has not given permission
     */
    function handleAutoPlay(hlsDevice) {
        if (hlsDevice?.videoElement?.paused) {
            try {
                hlsDevice?.videoElement?.play().then(
                    function() {
                        hlsDevice?.info.next({
                            type: hlsLogTypeEnum.autoPlay,
                            deviceId: hlsDevice?.deviceId,
                            value: true,
                            module: moduleName,
                            traceId: hlsDevice?.traceId
                        });
                    },
                    function() {
                        hlsDevice?.info.next({
                            type: hlsLogTypeEnum.autoPlay,
                            deviceId: hlsDevice?.deviceId,
                            value: false,
                            module: moduleName,
                            traceId: hlsDevice?.traceId
                        });
                    }
                );
            } catch (ignore) {
                hlsDevice?.info.next({
                    type: hlsLogTypeEnum.autoPlay,
                    deviceId: hlsDevice?.deviceId,
                    value: false,
                    module: moduleName,
                    traceId: hlsDevice?.traceId
                });
            }
        }
    }

    function onVideoPlaying() {
        const hlsDevice = hlsDevices[this?.deviceId];
        hlsDevice?.info?.next({
            type: hlsLogTypeEnum.videoPlaying,
            deviceId: hlsDevice?.deviceId
        });
    }

    function initStream(hlsDevice) {
        util.createMediaSession(
            hlsDevice,
            apiService,
            hlsLogTypeEnum
        ).subscribe({
            next: viewerSessionId => {
                // setting "muted" will allow autoPlay to work more often for users
                hlsDevice.videoElement.setAttribute('muted', '');
                hlsDevice.videoElement.setAttribute('playsinline', '');

                hlsDevice.videoElement.addEventListener('error', onVideoError);
                hlsDevice.videoElement.addEventListener('stalled', onVideoStalled);
                hlsDevice.videoElement.addEventListener('playing', onVideoPlaying);

                if (Hls.isSupported()) {
                    // use hls.js library
                    try {
                        hlsDevice.hls = new Hls({
                            xhrSetup: function (xhr/*, url*/) {
                                if (environment.oauthToken) {
                                    xhr.withCredentials = true; // do send cookies
                                    xhr.setRequestHeader('Authorization', 'Bearer ' + environment.oauthToken);
                                    xhr.setRequestHeader('AntiCSRF', 'AntiCSRF');
                                } else if (environment.sessionId) {
                                    xhr.withCredentials = true; // do send cookies
                                    xhr.setRequestHeader('Authorization', 'SeedonkSession ' + environment.sessionId);
                                    xhr.setRequestHeader('AntiCSRF', 'AntiCSRF');
                                }
                            }
                        });
                        hlsDevice.hls.deviceId = hlsDevice.deviceId;
                        hlsDevice.info.next({
                            type: hlsLogTypeEnum.libCreateInstanceConfirmed,
                            deviceId: hlsDevice.deviceId,
                            module: moduleName,
                            traceId: hlsDevice?.traceId
                        });
                    } catch (error) {
                        hlsDevice.info.error({
                            type: hlsLogTypeEnum.libCreateInstanceFailed,
                            error: error,
                            deviceId: hlsDevice.deviceId,
                            module: moduleName,
                            timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                            traceId: hlsDevice?.traceId
                        });
                        log({
                            code: hlsLogTypeEnum.libCreateInstanceFailed,
                            deviceId: hlsDevice.deviceId,
                            error: error,
                            int: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                            traceId: hlsDevice?.traceId
                        });
                        removeHlsDevice(hlsDevice);
                    }
                    hlsDevice.hls.on(Hls.Events.ERROR, onHlsError);
                    hlsDevice.hls.on(Hls.Events.MEDIA_ATTACHED, onHlsAttached);
                    hlsDevice.hls.on(Hls.Events.MANIFEST_PARSED, onHlsManifestParsed);
                    hlsDevice.hls.on(Hls.Events.BUFFER_APPENDED, onHlsBufferAppended);
                    hlsDevice.hls.on(Hls.Events.DESTROYING, onHlsDestroying);
                    hlsDevice.hls.loadSource(apiService.devices.m3u8({
                        deviceId: hlsDevice.deviceId,
                        sessionId: environment.sessionId,
                        viewerSessionId: viewerSessionId
                    }));
                    hlsDevice.hls.attachMedia(hlsDevice.videoElement);
                    handleAutoPlay(hlsDevice);
                } else if (hlsDevice.videoElement.canPlayType('application/vnd.apple.mpegurl')) {
                    // use built-in built-in support for HLS
                    hlsDevice.info.next({
                        type: hlsLogTypeEnum.builtInStart,
                        deviceId: hlsDevice.deviceId,
                        module: moduleName,
                        traceId: hlsDevice?.traceId
                    });
                    hlsDevice.videoElement.addEventListener('canplay', onCanPlay);
                    hlsDevice.videoElement.src = apiService.devices.m3u8({
                        deviceId: hlsDevice.deviceId,
                        sessionId: environment.sessionId,
                        viewerSessionId: viewerSessionId
                    });
                    handleAutoPlay(hlsDevice);
                }

                hlsDevice.keepAlive = util.getKeepalive(hlsDevice, apiService, hlsLogTypeEnum);
            },
            error: error => {
                hlsDevice.info.error({
                    type: hlsLogTypeEnum.viewerSessionFailed,
                    error: error,
                    module: moduleName,
                    usingHlsLib: Boolean(hlsDevice.hls),
                    timeSinceStart: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                    traceId: hlsDevice?.traceId
                });
                log({
                    code: hlsLogTypeEnum.viewerSessionFailed,
                    deviceId: hlsDevice.deviceId,
                    object: {
                        usingHlsLib: Boolean(hlsDevice.hls)
                    },
                    error: error,
                    int: _window.Date.now() - (hlsDevice?.startTime ?? 0),
                    traceId: hlsDevice?.traceId
                });
                removeHlsDevice(hlsDevice);
            }
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @property {Boolean} isSupported True if the client supports HLS Video playback
     * @name isSupported
     */
    function isSupported() {
        _isSupported ??= Boolean(
            Hls.isSupported()
            || _window?.document?.createElement('video')?.canPlayType('application/vnd.apple.mpegurl')
        );
        return _isSupported;
    }

    /**
     * @description Start HLS livestreaming
     * @param {String} deviceId
     * @param {HTMLVideoElement} videoElement The video element to display video in
     * @param {String} [traceId]
     * @returns {Observable} Emits info about streaming states
     *     that clients can use for logging and interacting with video.
     *     Values will always be an object with a type property of type HlsLogTypeEnum
     * @method
     * @name start
     */
    function start(deviceId, videoElement, traceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'startBadDeviceId',
                    traceId: traceId,
                    module: moduleName,
                    deviceId: deviceId,
                    object: videoElement
                })
            ));
        }
        if (!util.isValidVideoElement(videoElement)) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'startBadVideoElement',
                    traceId: traceId,
                    module: moduleName,
                    deviceId: deviceId,
                    object: videoElement
                })
            ));
        }
        if (!hlsDevices[deviceId]) {
            videoElement.deviceId = deviceId;
            hlsDevices[deviceId] = {
                info: new BehaviorSubject({
                    type: hlsLogTypeEnum.init,
                    deviceId: deviceId
                }),
                deviceId: deviceId,
                videoElement: videoElement,
                mediaSource: new _window.MediaSource(),
                hls: undefined,
                keepAlive: undefined,
                viewerSession: undefined,
                receivedFirstPacket: undefined,
                messageIndex: 0,
                startTime: _window.Date.now(),
                delay: 0,
                traceId: traceId ?? util.randomBase64Code(15),
                objectUrl: undefined
            };
        }
        initStream(hlsDevices[deviceId]);
        hlsDevices[deviceId].objectUrl = _window.URL.createObjectURL(hlsDevices[deviceId].mediaSource);
        hlsDevices[deviceId].videoElement.src = hlsDevices[deviceId].objectUrl;
        return hlsDevices[deviceId].info.asObservable();
    }

    /**
     * @description Stops livestreaming
     * @param {String} deviceId
     * @returns {Observable} Completes when the stream is ended
     * @method
     * @name stop
     */
    function stop(deviceId) {
        if (!deviceId || typeof deviceId !== 'string') {
            return new Observable(subscriber => {
                subscriber.error({
                    type: hlsLogTypeEnum.badParams,
                    module: moduleName
                });
            });
        }
        const hlsDevice = hlsDevices[deviceId];
        if (hlsDevice) {
            removeHlsDevice(hlsDevice);
            return hlsDevice.info.asObservable()
                .pipe(filter(x => x.type === hlsLogTypeEnum.libDestroying
                    || x.type === hlsLogTypeEnum.builtInStop), take(1));
        }
        return new Observable(subscriber => {
            subscriber.error({
                type: hlsLogTypeEnum.badParams,
                module: moduleName
            });
        });
    }

    /**
     * @description setDelay is not implemented for HLS, and will not do anything.
     * @param {String} deviceId The device to set extra delay for
     * @param {Number} value Seconds of extra delay
     * @method
     * @name setDelay
     */
    function setDelay(deviceId, value) {
        if (hlsDevices[deviceId]) {
            if (value === undefined || value === null || !isFinite(value)) {
                hlsDevices[deviceId].delay = 0;
            } else {
                hlsDevices[deviceId].delay = value;
            }
        }
    }

    return Object.freeze({
        /** @property {module:hlsVideoService.HlsLogTypeEnum} logTypeEnum */
        get logTypeEnum() { return hlsLogTypeEnum; },
        get isSupported() { return isSupported(); },
        start: start,
        stop: stop,
        setDelay: setDelay
    });
}

export default createHlsVideoService;
